import { useCallback, useEffect, useRef, useState } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Link } from "react-router-dom";
import { buildPage1, download_page1, PayslipDataTypes, payslipEmpty } from "./pay-slip-data";
import useApi from "../../core/customhook/useApi";
import { all_routes } from "../router/all_routes";
import Loader from "../../core/common/loader";
import { employee_id, get_payslip_by_id } from "../../environment";
import { DatePicker } from "antd";
import dayjs, { Dayjs } from "dayjs";
import Loading from "../../core/common/custom-loading/Loading";

const monthsData = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

// ─── Main Component ───────────────────────────────────────────────────────────
const ViewPaySlip = () => {
  const [formData, setFormData] = useState<PayslipDataTypes | null>(null);
  // const [loading, setLoading] = useState<boolean>(false);
  const currentDate = new Date();
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth() + 1; // 1–12

  const years = [currentYear - 1, currentYear, currentYear + 1];

  const [selectedMonth, setSelectedMonth] = useState<number | "">(
    currentMonth ?? "",
  );
  const [selectedYear, setSelectedYear] = useState<number | "">(
    currentYear ?? "",
  );

  const [page1Content, setPage1Content] = useState<string>("");
  const [downloading, setDownloading] = useState(false);

  const page1ContentRef = useRef<any>("");

  const {
    callApi,
    loading: loading,
    error: error,
  } = useApi("", {
    method: "GET",
    autoCall: false,
  });

  const downloadPDF = async () => {
    setDownloading(true);
    if (!formData) return;
    try {
      const doc = new jsPDF("p", "mm", "a4");

      const A4_W_MM = 210;
      const A4_H_MM = 297;
      const A4_W_PX = 794; // 210mm @ 96dpi
      let isFirstSlice = true;

      const renderPage = async (html: string) => {
        // ── 1. Mount off-screen but renderable ──
        const wrap = document.createElement("div");
        wrap.style.cssText = [
          "position:fixed",
          "top:0",
          "left:-9999px",
          `width:${A4_W_PX}px`,
          "background:#ffffff",
          "box-sizing:border-box",
          "font-family:'Times New Roman',serif",
          "font-size:14px",
          "color:#111",
        ].join(";");
        wrap.innerHTML = html;
        document.body.appendChild(wrap);

        // ── 2. Wait for fonts + layout ──
        await document.fonts.ready;
        await new Promise((r) => setTimeout(r, 150));

        // ── 3. Capture full height ──
        const canvas = await html2canvas(wrap, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
          width: A4_W_PX,
          height: wrap.scrollHeight, // capture full content height
          windowWidth: A4_W_PX,
          windowHeight: wrap.scrollHeight,
          logging: false,
        });
        document.body.removeChild(wrap);

        // ── 4. Slice canvas into A4 pages ──
        const A4_H_PX_SCALED = Math.floor((A4_H_MM / A4_W_MM) * A4_W_PX * 2);
        const totalHeight = canvas.height;
        let yOffset = 0;

        while (yOffset < totalHeight) {
          const sliceH = Math.min(A4_H_PX_SCALED, totalHeight - yOffset);
          if (sliceH < 10) break;

          const sliceCanvas = document.createElement("canvas");
          sliceCanvas.width = canvas.width;
          sliceCanvas.height = sliceH;
          const ctx = sliceCanvas.getContext("2d")!;
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, sliceCanvas.width, sliceH);
          ctx.drawImage(canvas, 0, -yOffset);

          if (!isFirstSlice) doc.addPage();
          doc.addImage(
            sliceCanvas.toDataURL("image/png"),
            "PNG",
            0,
            0,
            A4_W_MM,
            A4_H_MM,
          );
          yOffset += A4_H_PX_SCALED;
          isFirstSlice = false;
        }
      };

      // await renderPage(buildPage1(formData));
      await renderPage(download_page1(formData));

      doc.save(`pay_slip_${formData.employee.employee_name}.pdf`);
    } catch (err) {
      console.error("PDF error:", err);
      alert("PDF generation failed. Please try again.");
    } finally {
      setDownloading(false);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      // setLoading(true);
      const resp: any = await callApi(
        undefined,
        `${get_payslip_by_id}?employee_id=${employee_id}&salary_month=${selectedMonth}&salary_year=${selectedYear}`,
      );
      // console.log("resp:", resp);

      if (resp?.success) {
        const data1 = resp?.data || null;
        setFormData(data1);
        const html = buildPage1(data1);
        page1ContentRef.current = html;
        setPage1Content(html);
      }
    } catch (error) {
      console.log("error while fetching.", error);
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // console.log("formdetails:", formData);
  // console.log("table data:", getTableData(sequence_allowance, allowance_data));

  return (
    <div className="page-wrapper">
      <div className="content">
        <div
          style={{
            background: "#f9fafb",
            minHeight: "100vh",
            fontFamily: "'Segoe UI', sans-serif",
          }}
        >
          {/* ── Top bar ── */}
          <div
            className="d-sm-flex pb-2"
            style={{
              borderBottom: "1px solid #e5e7eb",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            {/* Breadcrumb */}
            <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
              <div className="my-auto mb-2">
                <h2 className="mb-1">Employee Pay Slip</h2>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item">
                      <Link to={all_routes.adminDashboard}>
                        <i className="ti ti-smart-home" />
                      </Link>
                    </li>
                    <li className="breadcrumb-item">Appraisal</li>
                    <li className="breadcrumb-item active">
                      Employee Pay Slip
                    </li>
                  </ol>
                </nav>
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                type="button"
                onClick={downloadPDF}
                className="btn btn-secondary"
                disabled={downloading || loading || !formData}
                style={{
                  // display: "flex",
                  // alignItems: "center",
                  // gap: "6px",
                  // padding: "7px 16px",
                  // borderRadius: "6px",
                  // border: "1px solid #d1d5db",
                  // background: "#fff",
                  // color: "#374151",
                  // fontSize: "13px",
                  // fontWeight: 600,
                  cursor:
                    downloading || loading || !formData
                      ? "not-allowed"
                      : "pointer",
                  opacity: downloading || loading || !formData ? 0.7 : 1,
                }}
              >
                <svg width="13" height="13" viewBox="0 0 16 16" fill="white">
                  <path
                    d="M8 1v9M4 7l4 4 4-4M2 13h12"
                    // stroke="#374151"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                &nbsp;
                {downloading ? "Generating…" : "Download PDF"}
              </button>
            </div>
          </div>

          <div className="card">
            <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
              <h5>Payslip List</h5>

              <div className="d-flex gap-2">
                {/* Month Dropdown */}
                <select
                  className="form-select form-select-sm py-0"
                  value={selectedMonth}
                  style={{ width: "max-content", height: 35 }}
                  onChange={(e) => setSelectedMonth(Number(e.target.value))}
                  disabled={loading || downloading}
                >
                  {monthsData?.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>

                {/* Year Dropdown */}
		  <DatePicker
                  picker="year"
                  inputReadOnly
                  value={!!selectedYear ? dayjs().year(selectedYear) : null}
                  style={{ height: 35 }}
                  disabledDate={(current) => {
                    if (!current) return false;
                    const year = current.year();
                    return year < 1990 || year > currentYear;
                  }}
                  placeholder="Select year"
                  onChange={(date: Dayjs | null) => {
                    setSelectedYear(date ? date.year() : "");
                  }}
                />
                {/*  <select
                  className="form-select"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(Number(e.target.value))}
                  disabled={loading || downloading}
                >
                  {years.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>  */}
              </div>
            </div>

            {/* ── Editor card ── */}
            <div className="p-4">
              <div
                style={{
                  background: "#fff",
                }}
              >
                {loading ? (
                  <Loading />
                ) : !formData ? (
                  <p className="text-dark px-4 py-3">No Data Found</p>
                ) : (
                  <div
                    ref={page1ContentRef}
                    dangerouslySetInnerHTML={{ __html: page1Content }}
                  />
                )}
                 {/* <div
                    ref={page1ContentRef}
                    dangerouslySetInnerHTML={{ __html: page1Content }}
                  /> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewPaySlip;
